import * as Popover from "@radix-ui/react-popover";
import { SmilePlus } from "lucide-react";
import React, { useState } from "react";
import { cn } from "../../lib/utils";
import { DEFAULT_EMOJI, EMOJI_CATEGORIES } from "./emojis";

export interface EmojiPickerProps {
  value?: string;
  onChange: (emoji: string) => void;
  className?: string;
  "aria-label"?: string;
  disabled?: boolean;
}

export function EmojiPicker({
  value,
  onChange,
  className,
  "aria-label": ariaLabel = "Choose emoji",
  disabled,
}: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(EMOJI_CATEGORIES[0].id);

  const current = value?.trim() || DEFAULT_EMOJI;
  const active = EMOJI_CATEGORIES.find((c) => c.id === tab) ?? EMOJI_CATEGORIES[0];

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "inline-flex items-center justify-center gap-1.5 min-h-[40px] min-w-[44px] px-2.5",
            "rounded-lg border border-input bg-background text-lg",
            "hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:opacity-50 disabled:pointer-events-none transition-colors",
            className
          )}
        >
          <span className="leading-none" aria-hidden>
            {current}
          </span>
          <SmilePlus size={14} className="text-muted-foreground opacity-70" aria-hidden />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[min(100vw-2rem,20rem)] rounded-xl border border-border bg-card text-card-foreground shadow-lg p-2.5 outline-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex gap-1 overflow-x-auto pb-2 mb-2 border-b border-border">
            {EMOJI_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setTab(c.id)}
                className={cn(
                  "shrink-0 px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
                  tab === c.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div
            className="grid grid-cols-8 gap-0.5 max-h-44 overflow-y-auto"
            role="listbox"
            aria-label={`${active.label} emojis`}
          >
            {active.emojis.map((emoji) => (
              <button
                key={emoji}
                type="button"
                role="option"
                aria-selected={emoji === current}
                title={emoji}
                onClick={() => {
                  onChange(emoji);
                  setOpen(false);
                }}
                className={cn(
                  "h-9 w-full rounded-md text-lg leading-none flex items-center justify-center",
                  "hover:bg-accent transition-colors",
                  emoji === current && "bg-primary/15 ring-1 ring-primary/40"
                )}
              >
                {emoji}
              </button>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export { EMOJI_CATEGORIES, DEFAULT_EMOJI } from "./emojis";
export type { EmojiCategory } from "./emojis";
